import React, { useState } from 'react';
import { Copy, Check, Tag, ArrowUpRight, Contact, MapPin } from 'lucide-react';
export type TabId = 'social' | 'personal' | 'notes';
interface InfluencerProfileProps {
  activeSection?: string | null;
  activeTab?: TabId;
  onTabChange?: (tab: TabId) => void;
  onSectionClick?: (section: string) => void;
}
export const InfluencerProfile: React.FC<InfluencerProfileProps> = ({
  activeSection,
  activeTab: controlledTab,
  onTabChange,
  onSectionClick
}) => {
  const [copied, setCopied] = useState(false);
  const [internalTab, setInternalTab] = useState<TabId>('social');
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = (tab: TabId) => {
    setInternalTab(tab);
    onTabChange?.(tab);
  };
  const handleCopy = () => {
    const email = 'john@abctalentagency.com';
    if (navigator.clipboard) {
      navigator.clipboard.writeText(email).catch(() => {});
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  // When a left-menu section is active, lift the matching region into a white
  // glassy popup and blur/dim everything else.
  const getHighlightClass = (section: string) => {
    if (!activeSection) return 'transition-all duration-300 rounded-lg';
    return activeSection === section ? 'relative z-20 bg-white/80 backdrop-blur-md rounded-xl ring-1 ring-white/60 shadow-[0_8px_30px_rgba(0,0,0,0.12)] p-3 -m-1 scale-[1.02] transition-all duration-300' : 'blur-[2px] opacity-40 transition-all duration-300';
  };
  const tabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'social',
    label: 'Social Media'
  }, {
    id: 'personal',
    label: 'Personal Information'
  }, {
    id: 'notes',
    label: 'Notes'
  }];
  return <div className="w-full bg-white rounded-xl border border-gray-200 shadow-float overflow-hidden text-left font-sans text-sm" data-id="element-872" data-mp="2nemksy8kgvp4">
      {/* Header */}
      <div className="p-4 md:p-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 relative" data-id="element-873" data-mp="1bxmu271vaox7">
        <div className={`flex items-center gap-4 ${getHighlightClass('influencer')}`} onClick={() => onSectionClick?.('influencer')} data-id="element-874" data-mp="rxcu998cac1l">
          <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-596.png" alt="Michelle Brown" className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover" data-id="element-875" data-mp="j1wxajcafgrs" />
          <div data-id="element-876" data-mp="92edc2wdnuzq">
            <h3 className="text-xl font-bold text-gray-900 mb-1" data-id="element-877" data-mp="45xp78p6hgmn">
              Michelle Brown
            </h3>
            <div className="flex items-center gap-2 mb-1" data-id="element-878" data-mp="3kj2j6irgpc">
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3 h-3" data-id="element-879" data-mp="66skocz06a7" />
              <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3 h-3" data-id="element-880" data-mp="1ufr6qhglnpyj" />
            </div>
            <p className="text-xs text-gray-600 mb-2" data-id="element-881" data-mp="221330luyp1o3">
              Paid Brand Promo • Affiliate
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-882" data-mp="26te59vd6czjt">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#FBEFC8] text-[#BE8725] text-[10px] font-medium" data-id="element-883" data-mp="26upalzm9qfd1">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-579.png" alt="US Flag" className="w-3 h-2 rounded-sm" data-id="element-884" data-mp="1e4h0mv1vz19j" />
                United States
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#FCE1C8] text-[#EB7535] text-[10px] font-medium" data-id="element-885" data-mp="1s36ywe0fz6zh">
                Creator
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#EAD2EE] text-[#A971B1] text-[10px] font-medium" data-id="element-886" data-mp="2zjcxu0qkvet8">
                Fashion
              </span>
            </div>
          </div>
        </div>

        <div className={`text-left md:text-right bg-gray-50 p-3 rounded-lg border border-gray-100 relative ${getHighlightClass('agency')}`} onClick={() => onSectionClick?.('agency')} data-id="element-887" data-mp="2x2lmq4pharci">
          {/* Highlight indicator for CTA */}
          <div className="absolute -top-3 -right-3 bg-zano-pink text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-md flex items-center gap-1 animate-bounce" data-id="element-888" data-mp="36z860p25pacf">
            Contact Manager <ArrowUpRight size={12} data-id="element-889" data-mp="3g0ofmul5d8fl" />
          </div>

          <h4 className="text-base font-bold text-gray-900 mb-1" data-id="element-890" data-mp="3uqnburt569sf">
            ABC Talent Agency
          </h4>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold mb-1.5" data-id="element-891" data-mp="3pqr6ao4sqaar">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-892" data-mp="7jbj8q92nnxa" />
            Exclusive representation
          </span>
          <p className="text-xs text-gray-600 mb-0.5" data-id="element-893" data-mp="m83dtqjc2kf9">John Marcus</p>
          <p className="text-xs text-gray-600 mb-0.5" data-id="element-894" data-mp="m8whgimzwtt6">+1-818-444-2311</p>
          <div className="text-xs text-gray-600 flex items-center md:justify-end gap-1.5 mt-1" data-id="element-895" data-mp="r5wmcq83zqip">
            <span className="font-medium text-zano-charcoal" data-id="element-896" data-mp="d6n4lm498ocm">
              john@abctalentagency.com
            </span>
            <button onClick={handleCopy} aria-label="Copy agency email" className={`p-1 rounded-md transition-colors ${copied ? 'bg-green-100 text-green-600' : 'bg-white border border-gray-200 text-gray-500 hover:text-zano-pink hover:border-zano-pink'}`} data-id="element-897" data-mp="i4oo285lepom">
              {copied ? <Check size={12} data-id="element-898" data-mp="3qki5cp7r056q" /> : <Copy size={12} data-id="element-899" data-mp="3unn3ygopjmq3" />}
            </button>
          </div>
          {copied && <p className="text-[10px] text-green-600 font-medium mt-0.5 md:text-right absolute right-3 -bottom-4" data-id="element-900" data-mp="3gof37jcwc6js">
              Copied!
            </p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-gray-50/50 border-b border-gray-100 px-4 md:px-6 flex gap-6 overflow-x-auto" data-id="element-901" data-mp="1r7jjtrc5fwtn">
        {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} aria-pressed={activeTab === tab.id} className={`py-3 text-xs whitespace-nowrap uppercase tracking-wide border-b-2 transition-colors ${activeTab === tab.id ? 'font-bold text-zano-pink border-zano-pink' : 'font-semibold text-gray-500 border-transparent hover:text-gray-700'}`} data-id="element-902" data-mp="1mfi1posavbvp">
            {tab.label}
          </button>)}
      </div>

      {/* Personal Information tab */}
      {activeTab === 'personal' && <div className="p-4 md:p-6 space-y-6" data-id="element-903" data-mp="1w638hzi4nmpp">
          <div data-id="element-904" data-mp="1w5b9sft8h8v7">
            <div className="flex items-center gap-1.5 mb-3" data-id="element-905" data-mp="1w5uto22g27bv">
              <Contact size={14} className="text-gray-800" data-id="element-906" data-mp="pqaod41u492v" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-907" data-mp="pno7gdt20h7z">
                Basic Info
              </h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-gray-100 pt-3" data-id="element-908" data-mp="anixvxq8kdfs">
              <div data-id="element-909" data-mp="flkjapb4wouf">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-910" data-mp="fn5ac4y8ppnj">Name</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-911" data-mp="kknd1dlnz8n3">
                  Marsha Glonen
                </p>
              </div>
              <div data-id="element-912" data-mp="pcouddxovpjz">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-913" data-mp="pe003jap37dq">Languages</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-914" data-mp="tcpd3artmmwu">
                  English, Spanish
                </p>
              </div>
              <div data-id="element-915" data-mp="13bolqtcl6iju">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-916" data-mp="13b53fbj0xs5a">Gender</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-917" data-mp="139u17g588sga">Female</p>
              </div>
              <div data-id="element-918" data-mp="188f1ffkhbc4q">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-919" data-mp="353b5qay29d6t">Age</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-920" data-mp="3554f3y026bii">28</p>
              </div>
            </div>
          </div>
          <div data-id="element-921" data-mp="1fuadov4xl25o">
            <div className="flex items-center gap-1.5 mb-3" data-id="element-922" data-mp="1ftqvddbdcbr4">
              <MapPin size={14} className="text-gray-800" data-id="element-923" data-mp="1frxpi08kxvk6" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-924" data-mp="1auec12jphmta">Location</h4>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-gray-100 pt-3" data-id="element-925" data-mp="1pj2777pn3uip">
              <div data-id="element-926" data-mp="1km20qvovopr2">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-927" data-mp="1klsijua2rmvx">Country</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-928" data-mp="wwhcpydvb2ax">
                  United States
                </p>
              </div>
              <div data-id="element-929" data-mp="ryfwrmbqnj3o">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-930" data-mp="15w4h8t1w8o87">State</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-931" data-mp="15u02fg2gi4t5">California</p>
              </div>
              <div data-id="element-932" data-mp="11t7v4sd5a9zi">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-933" data-mp="2hb9kby5xsosc">City</p>
                <p className="text-xs font-medium text-gray-900" data-id="element-934" data-mp="2h9gdoivbq4kc">Los Angeles</p>
              </div>
            </div>
          </div>
          <p className="text-[10px] text-gray-400 italic" data-id="element-935" data-mp="2kt725gc0ds6k">
            Personal information is filled out and maintained by the talent
            agency.
          </p>
        </div>}

      {/* Notes tab */}
      {activeTab === 'notes' && <div className="p-4 md:p-6" data-id="element-936" data-mp="2awazvscpe2ag">
          <div className="bg-gray-50 rounded-lg border border-gray-100 p-4 space-y-3" data-id="element-937" data-mp="2axdt46xi0gtj">
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-938" data-mp="2fw6w35f5spfg">
              Talent manager notes
            </p>
            <p className="text-xs text-gray-700 leading-relaxed" data-id="element-939" data-mp="38j1d1scrp6o6">
              Michelle Brown is a Los Angeles-based fashion and lifestyle
              creator known for her polished try-on hauls and authentic GRWM
              content. She over-indexes with a Gen-Z and young-millennial female
              audience in the US and UK, and consistently drives strong TikTok
              Shop conversion.
            </p>
            <div data-id="element-940" data-mp="3ra1737z4d6ob">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-941" data-mp="3rakgu1em8or9">
                Notable collaborations
              </p>
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-942" data-mp="szqanwcgfv31">
                Revolve (summer capsule launch), Sephora Squad, Fashion Nova,
                and a recurring ambassador partnership with a DTC skincare brand
                across 3 campaigns.
              </p>
            </div>
            <div data-id="element-943" data-mp="5a5ol4l08hpj">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-944" data-mp="59m3xg3yzf7t">
                Awards and recognition
              </p>
              <p className="text-xs text-gray-700 leading-relaxed" data-id="element-945" data-mp="a2f9253copkf">
                Finalist, 2024 Creator Economy Awards (Fashion). Featured in
                TikTok Rising Creators to Watch list.
              </p>
            </div>
            <div data-id="element-946" data-mp="1668gn3mzkwp">
              <p className="text-[11px] font-semibold text-gray-800 mb-1" data-id="element-947" data-mp="3igxnqpnsr3d0">
                Searchable keywords
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-948" data-mp="3nepehgcpgjfm">
                {['fashion', 'try-on haul', 'GRWM', 'TikTok Shop', 'affiliate', 'skincare', 'Gen-Z', 'US', 'UK', 'brand ambassador'].map(kw => <span key={kw} className="px-2 py-0.5 bg-white border border-gray-200 text-gray-600 rounded text-[10px] font-medium" data-id="element-949" data-mp="38sa7b8qz0pi3">
                    {kw}
                  </span>)}
              </div>
            </div>
            <p className="text-[10px] text-gray-400 italic pt-1" data-id="element-950" data-mp="bx7b8rcchkw">
              Notes are written by the talent manager. Keywords here power
              search when brands and agencies query the database.
            </p>
          </div>
        </div>}

      {/* Social Media tab */}
      {activeTab === 'social' && <div className="p-4 md:p-6 space-y-6" data-id="element-951" data-mp="5e60o684llnj">
          {/* Tags */}
          <div className={getHighlightClass('influencer')} onClick={() => onSectionClick?.('influencer')} data-id="element-952" data-mp="cguhditusvgl">
            <div className="flex items-center gap-1.5 mb-2" data-id="element-953" data-mp="hc9rbzoqq77q">
              <Tag size={14} className="text-gray-800" data-id="element-954" data-mp="mcxjwpsmaw42" />
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-955" data-mp="r45tpmxv0jhr">
                Content Style
              </h4>
            </div>
            <div className="flex flex-wrap gap-1.5" data-id="element-956" data-mp="w2h5423sz223">
              {['TikTok Shop', 'GRWM', 'Try-on Hauls'].map(tag => <span key={tag} className="px-2 py-1 bg-blue-50 text-blue-600 rounded text-xs font-medium" data-id="element-957" data-mp="103arus5ieate">
                  {tag}
                </span>)}
            </div>
          </div>

          <hr className="border-gray-100" data-id="element-958" data-mp="36wd7swup8wdj" />

          {/* TikTok Section */}
          <section className={getHighlightClass('analytics')} onClick={() => onSectionClick?.('analytics')} data-id="element-959" data-mp="3bueo5a0qqhlb">
            <div className="flex items-center justify-between mb-4" data-id="element-960" data-mp="34bx8sn66o8kf">
              <div className="flex items-center gap-1.5" data-id="element-961" data-mp="2zk5imdqxw4ut">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-4 h-4" data-id="element-962" data-mp="2ziknon0pqjwf" />
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-963" data-mp="3n9q3512qra67">TikTok</h4>
              </div>
              <span className="text-xs text-gray-600" data-id="element-964" data-mp="3ib6f8v4l7ob7">@michellethereal</span>
            </div>

            <div className="space-y-6" data-id="element-965" data-mp="3dbtssbhmoj95">
              {/* Analytics */}
              <div data-id="element-966" data-mp="3ddwtpqkbjwsn">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-967" data-mp="38cr07qiu45i2">
                  Live Analytics
                </h5>
                <div className="grid grid-cols-4 gap-2" data-id="element-968" data-mp="21xg8thpzwnye">
                  <div data-id="element-969" data-mp="1wxtuf4yfl1o0">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-970" data-mp="1wwitdcwde80l">
                      Followers
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-971" data-mp="3lq6p7u8h214d">2.2M</p>
                  </div>
                  <div data-id="element-972" data-mp="3qjspemxg8cja">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-973" data-mp="3qkawyfpkad6i">
                      AVG Views
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-974" data-mp="3gs53f1jnvnp7">50K</p>
                  </div>
                  <div data-id="element-975" data-mp="8cbous8w4mas">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-976" data-mp="8e69s6k5weev">
                      AVG Likes
                    </p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-977" data-mp="db6dwbxgb73c">4.5K</p>
                  </div>
                  <div data-id="element-978" data-mp="3ujtbmtqshs0w">
                    <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-979" data-mp="3drxudffwkbk">ENG Rate</p>
                    <p className="text-sm font-semibold text-gray-900" data-id="element-980" data-mp="3fuz5tm1lzvl">5.3%</p>
                  </div>
                </div>
              </div>

              {/* Demographics */}
              <div data-id="element-981" data-mp="2o5kbnzz9x9l4">
                <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-982" data-mp="2o1wix31nkr7g">
                  Audience Demographics
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-983" data-mp="11686sddpe3x4">
                  {/* Gender */}
                  <div data-id="element-984" data-mp="1f5rdd1qv977b">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-985" data-mp="1f4ycuiii3dxm">Gender</p>
                    <div className="flex items-center gap-3" data-id="element-986" data-mp="1b6s6vcphl2dr">
                      <div className="relative w-16 h-16 rounded-full shrink-0" style={{
                    background: 'conic-gradient(#77CCEA 0% 50%, #FF9CBE 50% 100%)'
                  }} data-id="element-987" data-mp="nfd2lee5gazv">
                        <div className="absolute inset-[8px] bg-white rounded-full" data-id="element-988" data-mp="rh834go9akcl"></div>
                      </div>
                      <div className="space-y-1 text-xs w-full" data-id="element-989" data-mp="3qubgr9l2nij">
                        <div className="flex items-center justify-between gap-2" data-id="element-990" data-mp="3vvy2m5osztqu">
                          <div className="flex items-center gap-1.5" data-id="element-991" data-mp="3vtvddnvh64n6">
                            <span className="w-2 h-2 rounded-full bg-[#77CCEA]" data-id="element-992" data-mp="1yus8vweua1fk"></span>
                            <span className="text-gray-600" data-id="element-993" data-mp="23su1b7y5nd3p">Male</span>
                          </div>
                          <span className="font-medium" data-id="element-994" data-mp="28ldi0ds52ocq">50%</span>
                        </div>
                        <div className="flex items-center justify-between gap-2" data-id="element-995" data-mp="2din5vozvqryg">
                          <div className="flex items-center gap-1.5" data-id="element-996" data-mp="1fx9admq0c17e">
                            <span className="w-2 h-2 rounded-full bg-[#FF9CBE]" data-id="element-997" data-mp="1kv0ys6rg156u"></span>
                            <span className="text-gray-600" data-id="element-998" data-mp="1pudl8qeeka8w">Female</span>
                          </div>
                          <span className="font-medium" data-id="element-999" data-mp="1utgt0ilroukk">50%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Country */}
                  <div data-id="element-1000" data-mp="368dituprznsz">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-1001" data-mp="367u9v3i3s9r3">Country</p>
                    <div className="space-y-2" data-id="element-1002" data-mp="ul0l3qdt46a9">
                      {[{
                    name: '🇺🇸 US',
                    val: 60
                  }, {
                    name: '🇬🇧 UK',
                    val: 30
                  }, {
                    name: '🇩🇪 DE',
                    val: 10
                  }].map(c => <div key={c.name} data-id="element-1003" data-mp="6v6898thmhq0">
                          <div className="flex justify-between text-[10px] mb-0.5" data-id="element-1004" data-mp="6u4p0h2biox7">
                            <span className="text-gray-700" data-id="element-1005" data-mp="1xe8jsmlprb9">{c.name}</span>
                            <span className="font-medium" data-id="element-1006" data-mp="3t3pd3yqtla7c">{c.val}%</span>
                          </div>
                          <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden" data-id="element-1007" data-mp="1wavyfmahb09g">
                            <div className="h-full bg-[#E66792] rounded-full" style={{
                        width: `${c.val}%`
                      }} data-id="element-1008" data-mp="1rb9lzf2h5o1p"></div>
                          </div>
                        </div>)}
                    </div>
                  </div>

                  {/* Age */}
                  <div data-id="element-1009" data-mp="vdvz5c58068a">
                    <p className="text-[10px] text-gray-500 mb-2" data-id="element-1010" data-mp="vd41lvs2byfd">Age</p>
                    <div className="h-20 flex items-end gap-1.5 pb-4 border-b border-gray-200 relative" data-id="element-1011" data-mp="he4eisk77rlb">
                      {[{
                    label: '13-17',
                    val: 4
                  }, {
                    label: '18-24',
                    val: 40
                  }, {
                    label: '25-34',
                    val: 20
                  }, {
                    label: '35-44',
                    val: 10
                  }, {
                    label: '45+',
                    val: 9
                  }].map(a => <div key={a.label} className="flex-1 flex flex-col items-center gap-0.5" data-id="element-1012" data-mp="4z6bfcmct67v">
                          <span className="text-[8px] font-medium text-gray-600" data-id="element-1013" data-mp="dwgqza67fhil">
                            {a.val}%
                          </span>
                          <div className="w-full max-w-[8px] bg-[#53B7AB] rounded-t-sm" style={{
                      height: `${a.val}px`
                    }} data-id="element-1014" data-mp="nvpeplah7zwi"></div>
                          <span className="absolute bottom-0 text-[8px] text-gray-500 translate-y-full pt-0.5" data-id="element-1015" data-mp="2ppexabb65vzt">
                            {a.label}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <hr className="border-gray-100" data-id="element-1016" data-mp="pwf0q85wmzh1" />

          {/* Pricing */}
          <div className={getHighlightClass('pricing')} onClick={() => onSectionClick?.('pricing')} data-id="element-1017" data-mp="kynarjotlnfh">
            <h5 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-1018" data-mp="3teyjmk2p0sz6">
              Pricing & Deliverables
            </h5>
            <div className="grid grid-cols-2 gap-4 mb-3" data-id="element-1019" data-mp="76yz1hihh0z1">
              <div data-id="element-1020" data-mp="78jquzdey5t7">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-1021" data-mp="74vy42fslnfj">Video post</p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-1022" data-mp="26axw30jj3r3">
                  $800 - $1,300
                </p>
              </div>
              <div data-id="element-1023" data-mp="1dn16fssfs7nv">
                <p className="text-[10px] text-gray-500 mb-0.5" data-id="element-1024" data-mp="1dnkfek03zlpr">Link in Bio</p>
                <p className="text-sm font-semibold text-gray-900" data-id="element-1025" data-mp="2vknsveogfxdr">$500</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded p-3 border border-gray-100" data-id="element-1026" data-mp="30jihwyd0mi6b">
              <p className="text-[10px] font-semibold text-gray-500 mb-1" data-id="element-1027" data-mp="30he9c3468uzp">
                Agency Notes
              </p>
              <p className="text-xs text-gray-700 whitespace-pre-line leading-relaxed" data-id="element-1028" data-mp="2rikeah6hoco9">
                Script - additional $400{'\n'}
                price includes 2 revisions{'\n'}
                additional revisions + $100 each{'\n'}7 days lead time, link in
                bio price for 24 hrs
              </p>
            </div>
          </div>
        </div>}
    </div>;
};